import React from 'react';
import { Link } from 'react-router-dom';
import { Factory, Cpu, ClipboardCheck, Boxes, Truck, Recycle, ArrowRight, CheckCircle2 } from 'lucide-react';
import PageHero from '../components/PageHero';

const steps = [
  { icon: Cpu, title: 'Design & Engineering', text: 'Every product begins with precision engineering using advanced LED chip technology and thermal management design.' },
  { icon: Boxes, title: 'Component Sourcing', text: 'We source premium-grade LED chips, drivers and heat sinks from trusted suppliers to ensure lasting performance.' },
  { icon: Factory, title: 'Precision Assembly', text: 'State-of-the-art assembly lines with automated SMD mounting guarantee consistency and quality in every unit.' },
  { icon: ClipboardCheck, title: 'Quality Testing', text: 'Each batch undergoes rigorous testing for brightness, flicker, thermal stability, and electrical safety.' },
  { icon: Recycle, title: 'Eco Compliance', text: 'Mercury-free, lead-free and RoHS-compliant manufacturing that protects both users and the environment.' },
  { icon: Truck, title: 'Packaging & Delivery', text: 'Secure packaging and nationwide logistics ensure products reach you safely and on time.' },
];

const quality = [
  'Advanced SMD LED chip technology',
  'Superior aluminium heat-sink design',
  'Flicker-free eye-comfort drivers',
  'Surge & short-circuit protection',
  'Up to 30,000+ hours lifespan',
  '100% pre-shipment quality inspection',
];

const Manufacturing = () => (
  <div>
    <PageHero title="Our Manufacturing" subtitle="World-class production processes engineered for quality, efficiency and durability." />

    <section className="max-w-7xl mx-auto px-4 py-16">
      <div className="text-center mb-12">
        <span className="text-[#37b34a] font-semibold uppercase tracking-wider text-sm">The Process</span>
        <h2 className="font-head text-3xl font-extrabold text-[#14181b] mt-2">From Concept to Illumination</h2>
        <div className="w-20 h-1 bg-[#37b34a] mx-auto mt-4 rounded-full" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {steps.map((s, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-7 card-hover relative">
            <span className="absolute top-5 right-6 font-head text-5xl font-extrabold text-[#eaf6ec]">{String(i + 1).padStart(2, '0')}</span>
            <div className="w-12 h-12 rounded-xl bg-[#0f7a37] flex items-center justify-center mb-4"><s.icon size={22} className="text-white" /></div>
            <h3 className="font-head font-bold text-lg text-[#14181b]">{s.title}</h3>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">{s.text}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="section-soft">
      <div className="max-w-7xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-[#37b34a] font-semibold uppercase tracking-wider text-sm">Quality Assurance</span>
          <h2 className="font-head text-3xl font-extrabold text-[#14181b] mt-2">Built to the Highest Standards</h2>
          <p className="text-gray-600 mt-4 leading-relaxed">Our commitment to excellence means every National Light product is manufactured with premium components and tested against strict quality benchmarks before it reaches you.</p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-3">
            {quality.map((q, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700"><CheckCircle2 size={18} className="text-[#0f7a37] shrink-0 mt-0.5" /> {q}</li>
            ))}
          </ul>
          <Link to="/shop" className="nl-btn bg-[#0f7a37] hover:bg-[#0d5228] text-white px-7 py-3 mt-7">View Our Products <ArrowRight size={18} /></Link>
        </div>
        <div className="hero-gradient rounded-3xl p-10 text-white relative overflow-hidden">
          <Factory size={200} className="absolute -right-8 -bottom-8 text-white/10" />
          <div className="relative z-10">
            <div className="font-head text-5xl font-extrabold">14</div>
            <div className="text-green-100">Months Warranty on Every Product</div>
            <div className="h-px bg-white/20 my-6" />
            <div className="font-head text-5xl font-extrabold">100%</div>
            <div className="text-green-100">Quality Inspected Before Shipping</div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default Manufacturing;
